import React, { useCallback, useEffect, useState } from "react";
import DataGrid, {
  Column,
  Selection,
  FilterRow,
  HeaderFilter,
  Sorting,
  Paging,
  ColumnChooser,
  GroupPanel,
  Pager,
  Summary,
  GroupItem,
} from "devextreme-react/data-grid";
import notify from "devextreme/ui/notify";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { useAuth0 } from "src/react-auth0-spa";
import { LoadIndicator, LoadPanel } from "devextreme-react";

interface Branch {
  name: string;
  commit: {
    author_name: string;
  };
}

const GitlabBranchesTable = () => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const { fetchNoResponse, fetchWithCheck } = useAuth0();

  const fetchBranches = useCallback(async () => {
    setLoading(true);
    fetchWithCheck("/recipeBranches")
      .then((recipeBranches) => {
        (window as any).recipeBranches = recipeBranches;
        setBranches(recipeBranches);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [fetchWithCheck]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const handleSelectionChange = (e: any) => {
    const selected = e.selectedRowsData.map((branch: Branch) => branch.name);
    setSelectedBranches(selected);
  };

  const deleteBranches = async () => {
    if (selectedBranches.length === 0) {
      notify("No branches selected.", "warning", 2500);
      return;
    }

    if (selectedBranches.length > 50) {
      notify(
        "You can only delete up to 50 branches at a time. Please select fewer branches.",
        "warning",
        3000
      );
      return;
    }

    const confirm = window.confirm(
      `Are you sure you want to PERMANENTLY delete ${
        selectedBranches.length === 1
          ? "this branch?"
          : `these ${selectedBranches.length} branches?`
      }`
    );
    if (!confirm) return;

    setDeleting(true);

    try {
      for (const branchName of selectedBranches) {
        const response = await fetchNoResponse(`/recipe/${branchName}`, {
          method: "DELETE",
        });
        if (!response.ok) {
          notify(
            `Failed to delete branch ${branchName}. Please try again.`,
            "error",
            3000
          );
          setDeleting(false);
          return;
        }
      }
      notify("Selected branches deleted successfully.", "success", 3000);
      fetchBranches(); // refresh
      setSelectedBranches([]);
    } catch (error) {
      console.error("Error archiving branches:", error);
      notify("Error archiving one or more branches.", "error", 3000);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div id="parentDiv">
      <Toolbar>
        <Item
          location="after"
          widget="dxButton"
          options={{
            text: "Delete Selected",
            onClick: deleteBranches,
            type: "danger",
            disabled: selectedBranches.length === 0,
          }}
        />
      </Toolbar>
      <DataGrid
        dataSource={branches}
        keyExpr="name"
        showBorders={true}
        onSelectionChanged={handleSelectionChange}
        columnAutoWidth={true}
        allowColumnReordering={true}
        hoverStateEnabled={true}
        wordWrapEnabled
        showColumnLines
        allowColumnResizing
        loadPanel={{ enabled: loading }}
        height={"calc(100vh - 140px)"}
      >
        <Selection mode="multiple" />
        <FilterRow visible={true} />
        <HeaderFilter visible={true} />
        <Sorting mode="multiple" />
        <Paging defaultPageSize={40} />
        <Pager
          showPageSizeSelector={true}
          allowedPageSizes={[20, 40, 100, 500]}
          showInfo={true}
        />

        <ColumnChooser enabled={true} />
        <GroupPanel visible={true} />
        <Column dataField="name" caption="Branch Name" />
        <Column dataField="commit.author_name" caption="Creator" />
        <Column dataField="commit.author_email" caption="Email" />
        <Column
          dataField="commit.committed_date"
          caption="Date"
          dataType={"date"}
        />
        <Column dataField="commit.title" caption="Title" />

        <Summary>
          <GroupItem
            column="name"
            summaryType="count"
            displayFormat="{0} branches"
          />
        </Summary>
      </DataGrid>
      {loading && (
        <div
          id="parentDiv"
          style={{
            display: "flex",
            justifyContent: "center",
            alignContent: "center",
            alignItems: "center",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.4)",
          }}
        >
          <div
            style={{
              width: "600px",
              height: "280px",
              background: "white",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              padding: "20px",
              borderRadius: "15px",
            }}
          >
            <div>Loading Recipes...</div>

            <div style={{ marginTop: "30px", marginBottom: "30px" }}>
              There are currently over 1400 branches. If you want this to take
              less time, please <strong>delete your old branches</strong>.
            </div>

            <LoadIndicator height={60} width={60} />
          </div>
        </div>
      )}
      <LoadPanel
        shadingColor="rgba(0,0,0,0.4)"
        position={{ of: "#parentDiv" }}
        visible={deleting}
        showIndicator={true}
        shading={true}
        // showPane={true}
      />
    </div>
  );
};

export default GitlabBranchesTable;
